/**
 * Renders an article's blocks.
 *
 * Headings for the four recognised kinds come from the translation catalog rather
 * than from the stored content, so their wording stays translatable; a free
 * section carries its own heading, written by the author.
 *
 * Solid yellow is kept for the one call to action an article may have. Step numbers
 * carry the tint the setup checklist gives its ticks, and notes are the app's Alert.
 */

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ArrowRight, Info, TriangleAlert } from "lucide-react"
import * as m from "motion/react-m"
import { Link } from "react-router"
import type { HelpBlock } from "../api/types"
import { railDraw } from "../motion"
import { ReferencedText } from "./ScreenReference"

const KIND_HEADINGS: Record<string, TranslationKeys> = {
  prerequisites: "HELPDESK/BLOCK_PREREQUISITES",
  steps: "HELPDESK/BLOCK_STEPS",
  afterward: "HELPDESK/BLOCK_AFTERWARD",
  issues: "HELPDESK/BLOCK_ISSUES",
}

/** Body copy: 15/1.6 in near-white, the reading size the brand sets for prose. */
const PROSE = "text-[15px] leading-[1.6] text-white/85"

/** The blocks that render a heading, in order — the article's table of contents. */
export function blockHeadings(
  blocks: HelpBlock[],
  translate: (key: TranslationKeys) => string,
): { anchor: string; label: string }[] {
  return blocks
    .filter((block) => block.anchor)
    .map((block) => ({
      anchor: block.anchor as string,
      label: block.heading ?? translate(KIND_HEADINGS[block.kind]),
    }))
}

function BlockHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-S text-[20px] leading-tight font-semibold tracking-[-0.2px] text-white">
      {children}
    </h2>
  )
}

function Prose({ text, className }: { text: string; className?: string }) {
  return (
    <div className={cn(PROSE, className)}>
      {text.split("\n\n").map((paragraph, index) => (
        <p key={index} className={index > 0 ? "mt-3" : undefined}>
          <ReferencedText text={paragraph} />
        </p>
      ))}
    </div>
  )
}

function Section({
  anchor,
  first,
  children,
}: {
  anchor?: string | null
  first: boolean
  children: React.ReactNode
}) {
  return (
    <section
      id={anchor ?? undefined}
      className={cn("scroll-mt-6", first ? "mb-L" : "my-L")}
    >
      {children}
    </section>
  )
}

function BlockBody({ block }: { block: HelpBlock }) {
  switch (block.kind) {
    case "prerequisites":
      return (
        <ul className="flex flex-col gap-2">
          {block.items.map((item) => (
            <li key={item} className="flex gap-3">
              <span
                aria-hidden="true"
                className="mt-[0.6em] size-1.5 shrink-0 rounded-full bg-grey5"
              />
              <span className={PROSE}>
                <ReferencedText text={item} />
              </span>
            </li>
          ))}
        </ul>
      )

    case "steps":
      return (
        <ol className="flex flex-col">
          {block.steps.map((step, index) => (
            <li key={step.title} className="relative flex gap-S pb-M last:pb-0">
              {/* The rail between numbers reads the list as one sequence, and
                  draws itself as the steps come into view. */}
              {index < block.steps.length - 1 ? (
                <m.span
                  aria-hidden="true"
                  initial={{ scaleY: 0 }}
                  whileInView={{ scaleY: 1 }}
                  viewport={{ once: true, margin: "0px 0px -10% 0px" }}
                  transition={railDraw(index)}
                  style={{ originY: 0 }}
                  className="absolute top-8 bottom-1 left-[13px] w-px bg-primary/30"
                />
              ) : null}
              <span
                aria-hidden="true"
                className="relative flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[13px] leading-none font-semibold text-primary"
              >
                {index + 1}
              </span>
              <div className="min-w-0 pt-0.5">
                <h3 className="mb-1 text-[15px] leading-snug font-semibold text-white">
                  {step.title}
                </h3>
                <Prose text={step.text} />
              </div>
            </li>
          ))}
        </ol>
      )

    case "issues":
      // Collapsed by default: a reader scans the symptoms and opens the one they
      // have, instead of reading every resolution in a row. The shared accordion
      // animates in CSS, where MotionConfig does not reach, so reduced motion is
      // set here for this page alone.
      return (
        <Accordion
          type="multiple"
          className="border-t motion-reduce:[&_[role=region]]:animate-none! motion-reduce:[&_svg]:transition-none"
        >
          {block.issues.map((issue) => (
            <AccordionItem key={issue.symptom} value={issue.symptom}>
              <AccordionTrigger className="gap-S text-[15px] font-medium text-white hover:no-underline">
                {issue.symptom}
              </AccordionTrigger>
              <AccordionContent>
                <Prose text={issue.resolution} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )

    case "callout": {
      const isWarning = block.tone === "warning"
      const Icon = isWarning ? TriangleAlert : Info
      return (
        // A standing note, not an alert: nothing to announce on every visit.
        <Alert
          role="note"
          className={cn(
            "border-0 bg-accent",
            isWarning &&
              "border border-warning/40 bg-warning/10 [&>svg]:text-warning",
          )}
        >
          <Icon aria-hidden="true" className="size-4" />
          <Prose text={block.text ?? ""} />
        </Alert>
      )
    }

    case "cta": {
      const action =
        block.href && block.label ? (
          <Button asChild size="sm">
            <Link to={block.href}>
              {block.label}
              <ArrowRight aria-hidden="true" />
            </Link>
          </Button>
        ) : null
      // A lone button in a card reads as an empty box; text is what earns the card.
      if (!block.text) return <div>{action}</div>
      return (
        <div className="rounded-lg border p-M">
          <Prose text={block.text} />
          <div className="mt-S">{action}</div>
        </div>
      )
    }

    default:
      return <Prose text={block.text ?? ""} />
  }
}

export function ArticleBlocks({ blocks }: { blocks: HelpBlock[] }) {
  const { translate } = useTranslation()
  const intro = blocks.find((block) => block.kind === "intro")
  const rest = blocks.filter((block) => block.kind !== "intro")

  return (
    <>
      {intro?.text ? (
        <Prose
          text={intro.text}
          className="mb-L text-[17px] leading-[1.6] text-grey5"
        />
      ) : null}
      {rest.map((block, index) => {
        const heading =
          block.heading ??
          (KIND_HEADINGS[block.kind]
            ? translate(KIND_HEADINGS[block.kind])
            : null)
        return (
          <Section
            key={`${block.kind}-${block.anchor ?? index}`}
            anchor={block.anchor}
            first={index === 0}
          >
            {heading ? <BlockHeading>{heading}</BlockHeading> : null}
            <BlockBody block={block} />
          </Section>
        )
      })}
    </>
  )
}
